/**
 * Popup de uma estação pluviométrica.
 *
 * Mostra as 24 h que o CEMADEN publica — e só elas. Não há janela a escolher:
 * oferecer "acumular em 1 h" sobre um dado de 24 h era uma escolha sem lastro
 * na fonte, e ainda fazia o gráfico se mexer junto com a animação.
 *
 * O desenho fica parado; o que anda com o player é a marca do instante. O
 * mouse lê qualquer barra, e os dados se atualizam sozinhos pelo ciclo
 * automático (`sync.ts`).
 */
import { useMemo } from 'react';
import { ExternalLink } from 'lucide-react';
import { formatLonLat } from '../lib/format';
import { formatDateTimeBoth } from '../lib/time';
import { GRAFICO_A, GraficoTempo } from '../ui/GraficoTempo';
import { urlGrafico } from './api';
import { useTimeStore } from '../state/time';
import { barras24h, type Barra } from './barras';
import { corDe, slotDe, totalDoPeriodo } from './series';
import { useStationStore } from './store';
import type { EstacaoFeature } from './layer';
import { ProximosDiasAqui } from '../previsao/ProximosDiasAqui';

/**
 * Histórico fixo de 24 h, com eixo de tempo e leitura no hover.
 *
 * O desenho NÃO se mexe: antes ele seguia o quadro exibido e o seletor de
 * janela ao mesmo tempo, e duas coisas mudando de uma vez tornavam impossível
 * ler qualquer uma. O que se move é só a marca do instante, uma linha sobre um
 * gráfico parado. A moldura (eixo, marca, leitura) é a mesma do nível dos rios.
 */
function Historico(props: { barras: Barra[] }) {
  const pares = props.barras;

  if (pares.length === 0) return <div className="text-xs text-ink-400">Sem histórico no período.</div>;

  const medidos = pares.filter((p) => p.mm !== null).map((p) => p.mm!);
  const pico = medidos.length ? Math.max(...medidos) : null;
  // O piso de 0,2 dá escala quando tudo é zero; anunciá-lo como pico
  // inventaria uma medição que não houve.
  const maior = Math.max(0.2, pico ?? 0);
  const A = GRAFICO_A;
  const TOCO = 1.4;

  return (
    <GraficoTempo
      instantes={pares.map((p) => p.ms)}
      iAtual={pares.findIndex((p) => p.atual)}
      rotulo={
        pico === null
          ? 'Sem medição nas últimas 24 horas'
          : pico > 0
            ? `Chuva nas últimas 24 horas, pico de ${pico.toFixed(1)} milímetros`
            : 'Sem chuva medida nas últimas 24 horas'
      }
      valor={(i) => {
        const mm = pares[i]!.mm;
        return mm === null ? null : `${mm.toFixed(1)} mm`;
      }}
      resumo={pico === null ? 'sem medição' : pico > 0 ? `pico ${pico.toFixed(1)} mm` : 'sem chuva'}
      desenho={({ w, destaque }) =>
        pares.map((p, i) => {
          const marcado = i === destaque;
          if (p.mm === null) {
            return (
              <rect key={i} x={i * w} y={A - 0.7} width={Math.max(0.6, w - 0.4)} height={0.7} fill="currentColor" opacity={marcado ? 0.5 : 0.18} />
            );
          }
          const h = p.mm > 0 ? Math.max(2, (A - TOCO - 1) * (p.mm / maior) + TOCO) : TOCO;
          return (
            <rect
              key={i}
              x={i * w}
              y={A - h}
              width={Math.max(0.6, w - 0.4)}
              height={h}
              fill={p.mm > 0 ? corDe(p.mm) : 'currentColor'}
              opacity={p.mm > 0 ? (marcado ? 1 : 0.85) : marcado ? 0.6 : 0.3}
            />
          );
        })
      }
    />
  );
}

export function StationPopup({ dado }: { dado: EstacaoFeature }) {
  const { estacao } = dado;
  const grade = useStationStore((s) => s.dados.grade);
  const linhaDe = useStationStore((s) => s.dados.linhaDe);

  const displayedTime = useTimeStore((s) => s.displayedTime);
  const currentTime = useTimeStore((s) => s.currentTime);
  const instante = displayedTime ?? currentTime;

  const linha = useMemo(() => {
    const i = linhaDe.get(estacao.cod);
    return grade && i !== undefined ? grade.v[i] : undefined;
  }, [grade, linhaDe, estacao.cod]);

  /**
   * O total das 24 h que o CEMADEN publica — não há janela a escolher.
   * Oferecer "acumular em 1 h" sobre um dado de 24 h era uma escolha sem
   * lastro na fonte.
   */
  const total = useMemo(
    () => (grade ? totalDoPeriodo(linha, grade.grandeza) : { mm: null, medidas: 0, fatias: 0 }),
    [grade, linha],
  );

  /**
   * O histórico é fixo: o período inteiro, agregado. Só a marca do instante
   * se move sobre ele, acompanhando o player.
   */
  const barras = useMemo<Barra[]>(() => {
    if (!grade || !linha) return [];
    const slot = instante === null ? -1 : slotDe(grade, instante);
    return barras24h(grade, linha, slot);
  }, [grade, linha, instante]);

  const mm = total.mm;

  return (
    <div className="space-y-1.5">
      <div className="text-xs text-ink-400">Estação · CEMADEN</div>
      <div className="leading-tight font-semibold text-white">
        {estacao.nome || estacao.cod}
        <div className="text-xs font-normal text-ink-300">
          {estacao.municipio}
          {estacao.uf && `/${estacao.uf}`}
        </div>
      </div>

      <div className="flex items-baseline gap-1.5 pt-0.5">
        {mm === null ? (
          <span className="text-sm text-ink-300">sem medição nas últimas 24 h</span>
        ) : (
          <>
            <span className="text-xl font-semibold tabular-nums" style={{ color: corDe(mm) }}>
              {mm.toFixed(1)}
            </span>
            <span className="text-xs text-ink-300">mm em 24 h</span>
          </>
        )}
      </div>
      <div className="pt-1 text-ink-200">
        <Historico barras={barras} />
      </div>

      {/* A previsão deste lugar (o "Próximos dias"). */}
      <ProximosDiasAqui lon={estacao.lon} lat={estacao.lat} />
      <div className="border-t border-white/10 pt-1 text-[11px] text-ink-400">
        {formatLonLat(estacao.lon, estacao.lat)} · código {estacao.cod}
        {estacao.id !== null && (
          <>
            {' · '}
            <a
              href={urlGrafico(estacao.id, estacao.uf)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-0.5 text-rio-300 hover:underline"
            >
              gráfico no CEMADEN
              <ExternalLink size={10} />
            </a>
          </>
        )}
      </div>
    </div>
  );
}

/** Texto do instante exibido, usado pelo painel. */
export function instanteLegivel(ms: number | null): string {
  return ms === null ? '—' : formatDateTimeBoth(ms);
}
